import { useCallback, useEffect, useMemo, useState } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getDbSupabase } from '../../lib/supabase';
import {
  PORTAL_ACTIVITY_PAGE_SIZE,
  fetchPortalActivityLog,
  fetchPortalActivityLogCount,
  portalActivityActionClass,
  portalActivityActionLabel,
  portalActivityEntityLabel,
  type PortalActivityAction,
  type PortalActivityRow,
} from '../../lib/portalActivityLog';
import { PORTAL_PRESENCE_LABELS, type PortalPresencePortal } from '../../lib/portalPresence';
import { formatSingaporeDateTime } from '../../utils/dateTime';

const PORTAL_FILTER_OPTIONS: Array<{ value: '' | PortalPresencePortal; label: string }> = [
  { value: '', label: 'All systems' },
  { value: 'crm', label: 'Sales CRM' },
  { value: 'it', label: 'IT Management' },
  { value: 'dem', label: 'Data Entry Management' },
  { value: 'forms', label: 'Application Form' },
  { value: 'report', label: 'Report' },
  { value: 'purchasing', label: 'Purchasing' },
  { value: 'admin', label: 'Settings' },
];

const ACTION_FILTER_OPTIONS: Array<{ value: '' | PortalActivityAction; label: string }> = [
  { value: '', label: 'All actions' },
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
  { value: 'create', label: 'Created' },
  { value: 'update', label: 'Updated' },
  { value: 'delete', label: 'Deleted' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'approve', label: 'Approved' },
  { value: 'reject', label: 'Rejected' },
];

function rowFromRealtimePayload(value: unknown): PortalActivityRow | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Partial<PortalActivityRow>;
  if (!row.id || !row.portal || !row.action || !row.entity_type || !row.title || !row.created_at) {
    return null;
  }
  return row as PortalActivityRow;
}

function matchesFilters(
  row: PortalActivityRow,
  portal: '' | PortalPresencePortal,
  action: '' | PortalActivityAction,
  search: string,
): boolean {
  if (portal && row.portal !== portal) return false;
  if (action && row.action !== action) return false;
  const q = search.trim().toLowerCase();
  if (!q) return true;
  const pool = [
    row.title,
    row.detail,
    row.actor_name,
    row.actor_email,
    row.entity_type,
    portalActivityEntityLabel(row.entity_type),
    portalActivityActionLabel(row.action),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return pool.includes(q);
}

export function ActivityLogPanel() {
  const [rows, setRows] = useState<PortalActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const [portal, setPortal] = useState<'' | PortalPresencePortal>('');
  const [action, setAction] = useState<'' | PortalActivityAction>('');
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const totalPages = Math.max(1, Math.ceil(totalCount / PORTAL_ACTIVITY_PAGE_SIZE));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * PORTAL_ACTIVITY_PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PORTAL_ACTIVITY_PAGE_SIZE, totalCount);

  const filterKey = `${portal}:${action}:${search}`;

  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    const safePage = Math.max(1, page);
    const offset = (safePage - 1) * PORTAL_ACTIVITY_PAGE_SIZE;

    try {
      const [list, count] = await Promise.all([
        fetchPortalActivityLog({
          limit: PORTAL_ACTIVITY_PAGE_SIZE,
          offset,
          portal,
          action,
          search,
        }),
        fetchPortalActivityLogCount({ portal, action, search }),
      ]);

      const nextTotalPages = Math.max(1, Math.ceil(count / PORTAL_ACTIVITY_PAGE_SIZE));
      if (safePage > nextTotalPages && count > 0) {
        setPage(nextTotalPages);
        return;
      }

      setRows(list);
      setTotalCount(count);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load activity log.');
    } finally {
      setLoading(false);
    }
  }, [page, portal, action, search]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const db = getDbSupabase('crm');
    let channel: RealtimeChannel | null = null;
    let timer: number | null = null;

    const scheduleRefresh = () => {
      if (page !== 1) return;
      if (timer != null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        void load();
      }, 300);
    };

    channel = db
      .channel('portal-activity-log-live')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'crm', table: 'portal_activity_log' },
        (payload) => {
          const row = rowFromRealtimePayload(payload.new);
          if (!row) {
            scheduleRefresh();
            return;
          }
          if (page !== 1) {
            setTotalCount((count) => count + 1);
            return;
          }
          if (!matchesFilters(row, portal, action, search)) return;
          setRows((current) => {
            if (current.some((item) => item.id === row.id)) return current;
            return [row, ...current].slice(0, PORTAL_ACTIVITY_PAGE_SIZE);
          });
          setTotalCount((count) => count + 1);
        },
      )
      .subscribe((status) => {
        setLive(status === 'SUBSCRIBED');
      });

    return () => {
      if (timer != null) window.clearTimeout(timer);
      if (channel) void db.removeChannel(channel);
      setLive(false);
    };
  }, [load, page, portal, action, search]);

  const pageLabel = useMemo(() => {
    if (totalCount === 0) return 'No entries';
    if (totalPages <= 1) return `${totalCount} entries`;
    return `${rangeStart}–${rangeEnd} of ${totalCount} · Page ${page} of ${totalPages}`;
  }, [page, rangeEnd, rangeStart, totalCount, totalPages]);

  return (
    <section className="card settings-panel activity-log-panel">
      <div className="settings-panel__head">
        <div>
          <h2 className="settings-panel__title">Activity log</h2>
        </div>
        <div className="activity-log-panel__actions">
          {live ? (
            <span className="online-users__live" title="Receiving live log entries on page 1">
              <span className="online-users__live-dot" aria-hidden />
              Live
            </span>
          ) : null}
          <span className="settings-panel__count">{pageLabel}</span>
          <button type="button" className="btn-secondary btn-sm" onClick={() => void load()}>
            Refresh
          </button>
        </div>
      </div>

      <div className="activity-log-filters">
        <label className="field activity-log-filters__field">
          <span className="field-label">System</span>
          <select
            className="field-input"
            value={portal}
            onChange={(e) => setPortal(e.target.value as '' | PortalPresencePortal)}
          >
            {PORTAL_FILTER_OPTIONS.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="field activity-log-filters__field">
          <span className="field-label">Action</span>
          <select
            className="field-input"
            value={action}
            onChange={(e) => setAction(e.target.value as '' | PortalActivityAction)}
          >
            {ACTION_FILTER_OPTIONS.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <form
          className="activity-log-filters__search"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(searchDraft.trim());
          }}
        >
          <label className="field">
            <span className="field-label">Search</span>
            <input
              className="field-input"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder="Search by user, account, title or details"
            />
          </label>
          <button type="submit" className="btn-secondary btn-sm">
            Search
          </button>
        </form>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}

      {loading ? (
        <div className="settings-loading">
          <div className="empty-state__spinner" />
          <p>Loading activity log…</p>
        </div>
      ) : rows.length === 0 ? (
        <p className="settings-empty">No activity recorded yet.</p>
      ) : (
        <>
          <div className="settings-table-wrap">
            <table className="settings-table activity-log-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>User</th>
                  <th>System</th>
                  <th>Action</th>
                  <th>What happened</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="activity-log-table__when" title={formatSingaporeDateTime(row.created_at)}>
                      {formatSingaporeDateTime(row.created_at)}
                    </td>
                    <td>
                      <div className="activity-log-table__user">
                        <strong>{row.actor_name?.trim() || '—'}</strong>
                        {row.actor_email ? (
                          <span className="activity-log-table__email">{row.actor_email}</span>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <span className={`online-users__portal online-users__portal--${row.portal}`}>
                        {PORTAL_PRESENCE_LABELS[row.portal]}
                      </span>
                    </td>
                    <td>
                      <span className={portalActivityActionClass(row.action)}>
                        {portalActivityActionLabel(row.action)}
                      </span>
                      <span className="activity-log-table__entity">
                        {portalActivityEntityLabel(row.entity_type)}
                      </span>
                    </td>
                    <td className="activity-log-table__title">{row.title}</td>
                    <td className="activity-log-table__detail">
                      {row.detail && row.detail !== '—' ? (
                        <pre className="activity-log-table__detail-pre">{row.detail}</pre>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div className="activity-log-pagination">
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </button>
              <span className="activity-log-pagination__label">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
